import { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import Typography from '@material-ui/core/Typography';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import { convertJST } from '../../../../utility/convertJST';
import {
  COLOR_BORDER,
  COLOR_CONTENT_BACKGROUND,
  COLOR_SHADOW,
  COLOR_TEXT_LINK,
  COLOR_TEXT_MAIN,
} from '../../../../theme/colors';
import { ExperimentOverview } from '../ExperimentOverview';
import { Project } from '../../../../generated/graphql';

/**
 * エクスペリエンス概要
 */
export const ExperimentListItem: FC<{ project: Project }> = ({ project }) => {
  const projectProductTypeMemo = useMemo(() => {
    if (project.product_type === 'private') {
      return 'プライベート';
    }
    if (project.product_type === 'rbr_categorized_limited_open') {
      return 'ターゲティング';
    }
    if (project.product_type === 'rbr_open') {
      return 'オープン';
    }
    return project.product_type;
  }, [project.product_type]);

  return (
    <StyledPaper>
      <ItemWrapper>
        {/* エクスペリエンス概要内_キャプチャ */}
        <PaperInnerLeft>
          {project.original_screenshot_image ? (
            <Img src={project.original_screenshot_image} />
          ) : (
            <ImageOutlinedIconMui fontSize="large" />
          )}
        </PaperInnerLeft>

        {/* エクスペリエンス概要内_各項目 */}
        <PaperInnerRight>
          <IdWrapper>
            <Id>
              ID: <Bold>{project.id}</Bold>
            </Id>
            {project.testing_experience === true && <IconBadgeSmall>検証用</IconBadgeSmall>}
            <IconBadge>{projectProductTypeMemo}</IconBadge>
          </IdWrapper>
          <TypographyMui>{project.title}</TypographyMui>
          <Url>
            <a href={project.url} target="_blank" rel="noreferrer">
              {project.url}
            </a>
          </Url>
          <NumberOfComment>
            <MarginRight>
              未読コメント: {project.variation_comment_counts ? project.variation_comment_counts?.unread : 0}
            </MarginRight>
            <FlexGrow>
              投稿数: {project.variation_comment_counts?.total ? project.variation_comment_counts.total : 0}
            </FlexGrow>
            <span>作成日: {convertJST(project.created_at)}</span>
          </NumberOfComment>

          {/* グレー背景エリア-概要 */}
          <ExperimentOverview project={project} />
        </PaperInnerRight>
      </ItemWrapper>
    </StyledPaper>
  );
};

const StyledPaper = styled('div')(`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  background: ${COLOR_CONTENT_BACKGROUND};
  box-shadow: 0px 1px 9px ${COLOR_SHADOW};
  border-radius: 8px;
  width: 100%;
  margin: 0 auto 24px auto;
  color: ${COLOR_TEXT_MAIN};
`);

const ItemWrapper = styled('div')(`
  display: flex;
  width: 100%;
`);

const PaperInnerLeft = styled('div')(`
  flex-basis: 256px;
  height: 192px;
  border: 1px solid ${COLOR_BORDER};
  margin: 0 24px 0 0;
  overflow: hidden;
  border-radius: 4px;
  background-color: #F2F2F2;
`);

const Img = styled('img')(`
  width: 100%;
  margin: auto;
`);

const ImageOutlinedIconMui = styled(ImageOutlinedIcon)(`
  margin: 80px auto;
  display: block;
`);

const PaperInnerRight = styled('div')(`
  flex-basis: calc(100% - 256px - 24px);
  height: 256px;
  max-height: 192px;
  position: relative;
`);

const IdWrapper = styled('div')(`
  display: flex;
  justifyContent: space-between;
`);

const Id = styled('div')(`
  flex-grow: 1;
`);

const IconBadge = styled('div')(`
  border: 1px solid ${COLOR_TEXT_MAIN};
  border-radius: 12px;
  height: 24px;
  flex-basis: 90px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
`);

const IconBadgeSmall = styled(IconBadge)(`
  flex-basis: 45px;
  margin-right: 16px;
`);

const Url = styled('div')(`
  line-height: 150%;
  margin-top: 10.5px;
  & a {
    color: ${COLOR_TEXT_LINK};
    line-height: 150%;
    text-decoration: none;
  }
`);

const NumberOfComment = styled('div')(`
  line-height: 150%;
  margin-top: 10.5px;
  align-items: center;
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
`);

const TypographyMui = styled(Typography)(`
  font-family: Hiragino Sans;
  font-weight: 700;
  font-size: 18px;
  line-height: 150%;
  color: ${COLOR_TEXT_MAIN};
`);

const MarginRight = styled('span')(`
  margin-right: 24px;
`);

const FlexGrow = styled('span')(`
  flex-grow: 1;
`);

const Bold = styled('span')(`
  font-weight: bold;
`);
